import * as React from 'react';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Download,
  User as UserIcon,
} from 'lucide-react';
import type { Inquiry, InquiryListQuery, InquiryListResponse } from '@proptr/shared';
import { AppShell } from '../components/AppShell.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu.js';
import { cn, initials } from '../lib/utils.js';
import { client } from '../main.js';

type QualificationKey = NonNullable<InquiryListQuery['qualification']>;
type SortKey = 'createdAt' | 'name';
type SortOrder = 'asc' | 'desc';

const QUALIFICATION_OPTIONS: { value: QualificationKey; label: string }[] = [
  { value: 'all', label: 'All inquiries' },
  { value: 'relevant', label: 'Relevant inquiries' },
  { value: 'double-qualified', label: 'Double-qualified' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'pre-qualified', label: 'Pre-qualified' },
  { value: 'unqualified', label: 'Unqualified' },
];

const PAGE_SIZE_OPTIONS = [
  { value: '10', label: '10' },
  { value: '25', label: '25' },
  { value: '50', label: '50' },
];

export function InquiriesPage() {
  const [qualification, setQualification] = React.useState<QualificationKey>(
    'all',
  );
  const [sortBy, setSortBy] = React.useState<SortKey>('createdAt');
  const [order, setOrder] = React.useState<SortOrder>('desc');
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [page, setPage] = React.useState<number>(1);

  const [data, setData] = React.useState<InquiryListResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [exporting, setExporting] = React.useState(false);
  const [exportError, setExportError] = React.useState<string | null>(null);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await client.inquiries.exportCsv({
        qualification,
        sort: sortBy,
        order,
      });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inquiries-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  React.useEffect(() => {
    let alive = true;
    setError(null);
    client.inquiries
      .list({ qualification, sort: sortBy, order, page, limit: pageSize })
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      });
    return () => {
      alive = false;
    };
  }, [qualification, sortBy, order, page, pageSize]);

  const headingLabel =
    QUALIFICATION_OPTIONS.find((o) => o.value === qualification)?.label ??
    'Inquiries';

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setOrder('desc');
    }
  };

  const meta = data?.meta;
  const total = meta?.total ?? 0;
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const totalPages = meta?.pages ?? 1;
  const items = data?.items ?? null;

  return (
    <AppShell>
      <div className="space-y-6 pb-10">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
              Inquiries
            </h1>
            <p className="mt-3 font-mono text-[11px] text-muted-foreground">
              <span className="text-foreground">{meta?.all ?? '—'}</span> total
              {meta && (
                <>
                  {' · '}
                  <span className="text-foreground">
                    {meta.sources.listing}
                  </span>{' '}
                  listing
                  {' · '}
                  <span className="text-foreground">
                    {meta.sources.button}
                  </span>{' '}
                  button
                </>
              )}
            </p>
            {exportError && (
              <p className="mt-2 font-mono text-[11px] text-destructive">
                {exportError}
              </p>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleExport}
            disabled={exporting}
          >
            <Download />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </div>

        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <div className="min-w-[1200px]">
              <div className="flex items-center gap-4 border-b border-[hsl(var(--hairline))] px-5 py-3">
                <span className="h-8 w-8 shrink-0" aria-hidden />
                <span className="h-10 w-10 shrink-0" aria-hidden />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="mono-label group flex min-w-[200px] flex-[2.5] items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground"
                    >
                      <span>{headingLabel}</span>
                      <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[14rem]">
                    {QUALIFICATION_OPTIONS.map((opt) => (
                      <DropdownMenuItem
                        key={opt.value}
                        onSelect={() => {
                          setQualification(opt.value);
                          setPage(1);
                        }}
                        className={cn(
                          opt.value === qualification && 'text-foreground',
                        )}
                      >
                        {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <HeaderLabel className="min-w-[140px] flex-[1.5]">Channel</HeaderLabel>
                <HeaderLabel className="min-w-[180px] flex-[2]">Email</HeaderLabel>
                <HeaderLabel className="min-w-[160px] flex-[2]">Source</HeaderLabel>
                <HeaderLabel className="min-w-[80px] flex-1">Form Q&amp;A</HeaderLabel>
                <SortHeader
                  label="Created"
                  sortKey="createdAt"
                  sortBy={sortBy}
                  order={order}
                  onSort={handleSort}
                  className="min-w-[120px] flex-[1.5]"
                />
              </div>

              {error ? (
                <div className="px-5 py-12 text-center font-mono text-[11px] text-destructive">
                  {error}
                </div>
              ) : items === null ? (
                <div className="px-5 py-12 text-center font-mono text-[11px] text-muted-foreground">
                  Loading…
                </div>
              ) : items.length === 0 ? (
                <div className="px-5 py-12 text-center font-mono text-[11px] text-muted-foreground">
                  No inquiries match the selected filters.
                </div>
              ) : (
                items.map((inquiry) => (
                  <InquiryRow key={inquiry._id} inquiry={inquiry} />
                ))
              )}
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[hsl(var(--hairline))] px-5 py-3.5">
            <div className="mono-label text-muted-foreground">
              Showing{' '}
              <span className="text-foreground">
                {start}–{end}
              </span>{' '}
              of <span className="text-foreground">{total}</span> results
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="mono-label text-muted-foreground">Page limit</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline">
                      {pageSize}
                      <ChevronDown />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[5rem]">
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                      <DropdownMenuItem
                        key={opt.value}
                        onSelect={() => {
                          setPageSize(Number(opt.value));
                          setPage(1);
                        }}
                        className={cn(
                          String(pageSize) === opt.value && 'text-foreground',
                        )}
                      >
                        {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}

function InquiryRow({ inquiry }: { inquiry: Inquiry }) {
  const [open, setOpen] = React.useState(false);

  const clientName = inquiry.client?.name || '—';
  const clientPhone = inquiry.client?.phone || '—';
  const clientEmail = inquiry.client?.email || '—';
  const rawListingName = inquiry.listing?.name || inquiry.listing?.projectName;
  const rawListingRef = inquiry.listing?.externalId;
  const isButtonInquiry =
    isButtonPlaceholder(rawListingName) ||
    isButtonPlaceholder(rawListingRef) ||
    isButtonPlaceholder(inquiry.listingExternalId);
  const listingLabel = isButtonInquiry
    ? 'Button inquiry'
    : rawListingName;
  const listingRef = isButtonInquiry ? undefined : rawListingRef;
  const answered = countAnswered(inquiry);
  const totalQ = inquiry.formQuestionCount ?? 0;
  const createdAt =
    inquiry.createdAt && typeof inquiry.createdAt === 'string'
      ? inquiry.createdAt.slice(0, 10)
      : '—';

  return (
    <div
      className={cn(
        'border-b border-[hsl(var(--hairline))] last:border-b-0',
        open && 'bg-muted/20',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/60"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground">
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 transition-transform',
              open && 'rotate-180',
            )}
          />
        </span>
        <ClientAvatar name={clientName} image={inquiry.client?.image} />
        <div className="min-w-[200px] flex-[2.5]">
          <div className="truncate font-sans text-[14px] font-medium text-foreground">
            {clientName}
          </div>
          <div className="truncate font-mono text-[11px] text-muted-foreground">
            {clientPhone}
          </div>
        </div>
        <div className="min-w-[140px] flex-[1.5]">
          <ChannelBadge channel={inquiry.channel} />
        </div>
        <RowCell
          className="min-w-[180px] flex-[2]"
          value={clientEmail}
          dim={clientEmail === '—' || clientEmail === ''}
        />
        <div className="min-w-[160px] flex-[2]">
          {listingLabel || listingRef ? (
            <>
              <div className="truncate font-sans text-[13px] font-medium text-foreground">
                {listingLabel || '—'}
              </div>
              {listingRef && (
                <div className="truncate mono-label text-muted-foreground">
                  {listingRef}
                </div>
              )}
            </>
          ) : (
            <div className="font-mono text-[12px] text-muted-foreground">—</div>
          )}
        </div>
        <div className="min-w-[80px] flex-1">
          <div
            className={cn(
              'font-mono text-[13px] font-medium tabular-nums',
              totalQ === 0 && 'text-muted-foreground',
            )}
          >
            {totalQ > 0 ? `${answered}/${totalQ}` : '—'}
          </div>
        </div>
        <div className="min-w-[120px] flex-[1.5]">
          <div className="font-mono text-[12px] text-foreground/80 tabular-nums">
            {createdAt}
          </div>
        </div>
      </button>
      {open && <InquiryDetail inquiry={inquiry} />}
    </div>
  );
}

function RowCell({
  value,
  className,
  dim,
}: {
  value: string;
  className?: string;
  dim?: boolean;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <div
        className={cn(
          'truncate font-sans text-[13px] font-medium text-foreground',
          dim && 'text-muted-foreground',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function HeaderLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mono-label text-muted-foreground', className)}>
      {children}
    </div>
  );
}

function countAnswered(inquiry: Inquiry): number {
  const raw = inquiry.answeredQuestions;
  if (!raw || typeof raw !== 'object' || !inquiry.formId) return 0;
  const formBucket = (raw as Record<string, unknown>)[inquiry.formId];
  if (!formBucket || typeof formBucket !== 'object') return 0;
  return Object.keys(formBucket as Record<string, unknown>).filter(
    (k) => k !== 'completed' && k !== 'started',
  ).length;
}

function InquiryDetail({ inquiry }: { inquiry: Inquiry }) {
  const answers = extractAnswers(inquiry);
  const origin =
    inquiry.proptreeOrigin?.profileFirstName ||
    inquiry.proptreeOrigin?.alias ||
    inquiry.proptreeOrigin?.shortId;
  const rawListingName =
    inquiry.listing?.name || inquiry.listing?.projectName;
  const rawListingExt =
    inquiry.listing?.externalId || inquiry.listingExternalId;
  const isButtonInquiry =
    isButtonPlaceholder(rawListingName) || isButtonPlaceholder(rawListingExt);
  const sourceValue = isButtonInquiry
    ? 'Button inquiry'
    : rawListingName || rawListingExt;

  const leftRows: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'Name', value: inquiry.client?.name || '—' },
    { label: 'Email', value: inquiry.client?.email || '—' },
    { label: 'Phone', value: inquiry.client?.phone || '—' },
    { label: 'Channel', value: <ChannelBadge channel={inquiry.channel} /> },
  ];
  if (sourceValue) {
    leftRows.push({ label: 'Source', value: sourceValue });
  }
  // Agent row intentionally omitted in the agent portal — it's always
  // the signed-in user, so it's noise. Manager portal still shows it.
  if (origin) {
    leftRows.push({ label: 'Source proptree', value: origin });
  }
  if (inquiry.type?.name) {
    leftRows.push({ label: 'Qualification', value: inquiry.type.name });
  }
  if (inquiry.rating?.name) {
    leftRows.push({ label: 'Rating', value: inquiry.rating.name });
  }
  if (inquiry.followUp?.name) {
    leftRows.push({ label: 'Follow-up', value: inquiry.followUp.name });
  }

  return (
    <div className="border-t border-[hsl(var(--hairline))] bg-muted/40 px-5 py-5">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Left: key/value rows */}
        <div className="divide-y divide-[hsl(var(--hairline))] overflow-hidden rounded-lg border border-[hsl(var(--hairline))] bg-card">
          {leftRows.map((r) => (
            <div
              key={r.label}
              className="grid grid-cols-[120px_1fr] gap-4 px-5 py-3.5"
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
                {r.label}
              </div>
              <div className="font-sans text-[14px] font-medium text-foreground">
                {r.value}
              </div>
            </div>
          ))}
        </div>

        {/* Right: form answers */}
        <div className="space-y-2.5">
          {answers.length > 0 ? (
            <>
              <div className="inline-flex rounded-md bg-primary/10 px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-primary">
                Form answers
              </div>
              {answers.map((a) => (
                <AnswerCard key={a.id} answer={a} />
              ))}
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-[hsl(var(--hairline))] bg-card px-5 py-6 text-center font-mono text-[11px] text-muted-foreground">
              No form answers for this inquiry.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface NormalizedAnswer {
  id: string;
  question: string;
  answer: string;
  date?: string;
}

function extractAnswers(inquiry: Inquiry): NormalizedAnswer[] {
  const raw = inquiry.answeredQuestions;
  if (!raw || typeof raw !== 'object' || !inquiry.formId) return [];
  const outer = raw as Record<string, unknown>;
  const formBucket = outer[inquiry.formId];
  if (!formBucket || typeof formBucket !== 'object') return [];
  const items: NormalizedAnswer[] = [];
  for (const [key, val] of Object.entries(
    formBucket as Record<string, unknown>,
  )) {
    if (key === 'completed' || key === 'started') continue;
    if (!val || typeof val !== 'object') continue;
    const v = val as {
      id?: number | string;
      question?: string;
      answer?: unknown;
      date?: string;
    };
    items.push({
      id: String(v.id ?? key),
      question: v.question ?? '—',
      answer:
        typeof v.answer === 'string'
          ? v.answer
          : v.answer != null
            ? String(v.answer)
            : '—',
      date: v.date,
    });
  }
  items.sort((a, b) => {
    const ad = a.date ?? '';
    const bd = b.date ?? '';
    return ad.localeCompare(bd);
  });
  return items;
}

function AnswerCard({ answer }: { answer: NormalizedAnswer }) {
  const ts = answer.date ? formatTime(answer.date) : null;
  return (
    <div className="rounded-lg border border-[hsl(var(--hairline))] bg-primary/5 px-5 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="mono-label text-muted-foreground">Question</div>
        {ts && (
          <div className="font-mono text-[10px] text-muted-foreground">{ts}</div>
        )}
      </div>
      <div className="mt-1 font-sans text-[14px] font-medium text-primary">
        {answer.question}
      </div>
      <div className="mt-3 mono-label text-muted-foreground">Answer</div>
      <div className="mt-1 font-sans text-[14px] font-medium text-primary">
        {answer.answer}
      </div>
    </div>
  );
}

function isButtonPlaceholder(value: unknown): boolean {
  return typeof value === 'string' && value === '###BUTTON###';
}

function ChannelBadge({ channel }: { channel?: string | null }) {
  if (!channel) {
    return (
      <span className="font-sans text-[14px] font-medium text-muted-foreground">
        —
      </span>
    );
  }
  const label = channel.charAt(0).toUpperCase() + channel.slice(1);
  return (
    <span className="truncate font-sans text-[14px] font-medium text-foreground">
      {label}
    </span>
  );
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const date = d.toISOString().slice(0, 10);
    const time = d.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return `${date} ${time.toLowerCase()}`;
  } catch {
    return '';
  }
}

function ClientAvatar({
  name,
  image,
}: {
  name: string;
  image?: string | null;
}) {
  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className="h-10 w-10 shrink-0 rounded-full bg-muted object-cover"
      />
    );
  }
  if (name && name !== '—') {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-[11px] font-bold uppercase">
        {initials(name)}
      </div>
    );
  }
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
      <UserIcon className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

function QualificationPill({
  value,
  label,
}: {
  value?: string | null;
  label?: string | null;
}) {
  if (!value) return <span className="pill pill-muted">—</span>;
  const cls =
    value === 'double-qualified'
      ? 'pill pill-success'
      : value === 'qualified'
        ? 'pill pill-accent'
        : value === 'pre-qualified'
          ? 'pill pill-warning'
          : value === 'unqualified'
            ? 'pill pill-muted'
            : 'pill pill-muted';
  return <span className={cls}>{label || value}</span>;
}

function FollowUpPill({ value }: { value?: string | null }) {
  if (value === 'followed_up')
    return <span className="pill pill-success">Done</span>;
  if (value === 'pending_follow_up')
    return <span className="pill pill-warning">Pending</span>;
  return <span className="pill pill-muted">Not yet</span>;
}

function SortHeader({
  label,
  sortKey,
  sortBy,
  order,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  sortBy: SortKey;
  order: SortOrder;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const active = sortBy === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={cn(
        'mono-label flex items-center justify-center gap-1 text-muted-foreground transition-colors hover:text-foreground',
        active && 'text-foreground',
        className,
      )}
    >
      <span>{label}</span>
      {active &&
        (order === 'asc' ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        ))}
    </button>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="mono-label text-muted-foreground">{label}</div>
      <div className="mt-1 truncate font-mono text-[13px] font-semibold tabular-nums">
        {value}
      </div>
    </div>
  );
}
